/**
 * usePlacements - TanStack Query hooks for the Kunde Ausschreibungen tab + the Placement detail
 * screen (P2 Placement Slice 4).
 *
 * Server state = TanStack Query ONLY (coding-standards §3). live mode hits the real endpoints via
 * apiClient.placements.*; mock mode resolves lightweight fixtures so the tab + screen render
 * standalone (no backend). The server owns every decision (validation, number allocation, KPIs,
 * transitions) - the UI only triggers + renders.
 *
 * [Source: apps/web/src/features/entity-detail/hooks/useRisikoprofil.ts] (live/mock + qk + invalidate idiom)
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { placementKpisFromItems, PLACEMENT_FIRM_LIST_DEFAULT_LIMIT } from '@brokly/shared';
import type { PlacementCreateBody, PlacementDetail, PlacementListResponse, PlacementListView } from '@brokly/shared';
import type { PlacementFirmListResponse } from './types';

type SubjectType = 'person' | 'firma';

// ── Mock fixtures (mock mode only) ──────────────────────────────────────────────

function mockList(subjectType: SubjectType, subjectId: string): PlacementListResponse {
  return { subject_type: subjectType, subject_id: subjectId, items: [], kpis: placementKpisFromItems([]) };
}

function mockFirmList(): PlacementFirmListResponse {
  // Firm-wide scope: no single subject on the envelope. One page, zero counts, no next page.
  return {
    subject_type: null,
    subject_id: null,
    items: [],
    kpis: placementKpisFromItems([]),
    view_counts: { alle: 0, offen: 0, erledigt: 0, abgebrochen: 0 },
    page: { next_cursor: null, limit: PLACEMENT_FIRM_LIST_DEFAULT_LIMIT },
  };
}

function mockDetail(id: string, body?: PlacementCreateBody): PlacementDetail {
  const now = new Date().toISOString();
  return {
    id,
    placement_number: 'PL-2026-000000',
    subject_type: body?.subject_type ?? 'firma',
    subject_id: body?.subject_id ?? 'mock-subject',
    policy_id: body?.policy_id ?? null,
    policy_number: null,
    carrier_name: null,
    insurance_object_ref: body?.insurance_object_ref ?? null,
    branchencode: body?.branchencode ?? '60000',
    branchen_label: 'Diverse',
    anlass: body?.anlass ?? 'neugeschaeft',
    status: 'entwurf',
    status_changed_at: now,
    closure_status: 'offen',
    closure_start_zeitpunkt: now,
    closure_end_zeitpunkt: null,
    gewuenschter_beginn: body?.gewuenschter_beginn ?? null,
    betreuer_user_id: 'mock-user',
    betreuer_name: 'Sie',
    created_at: now,
    updated_at: now,
    abbruch_grund: null,
    abgebrochen_at: null,
    created_by: 'mock-user',
    closure: {
      status: 'offen',
      start_zeitpunkt: now,
      end_zeitpunkt: null,
      resulting_policy_id: null,
      resulting_policy_number: null,
      comparison_result: null,
      comparison_details_ref: null,
      comparison_documented_at: null,
      abschluss_art: null,
      policy_zugestellt_at: null,
      policy_zustellung_channel: null,
    },
    erlaubte_transitionen: ['risikoprofil_erfasst', 'abgebrochen'],
    pflichtdeckung: { warnflag: false, warn_at: null, warn_rule_keys: [], ack_at: null, ack_by: null },
  };
}

// ── Queries ──────────────────────────────────────────────────────────────────────

/**
 * The Placements for one customer. `subjectType` undefined disables the fetch (the E5 gate: a
 * role that denies the tab must not even request the data).
 */
export function usePlacementsForKunde(subjectId: string, subjectType: SubjectType | undefined) {
  const live = isLiveData();
  return useQuery<PlacementListResponse>({
    queryKey: qk.placements(subjectType ?? 'none', subjectId, {}, live ? 'live' : 'mock'),
    enabled: Boolean(subjectType),
    queryFn: () =>
      live && subjectType
        ? apiClient.placements.listForCustomer(subjectType, subjectId)
        : Promise.resolve(mockList(subjectType ?? 'firma', subjectId)),
    ...(live
      ? {}
      : { initialData: () => mockList(subjectType ?? 'firma', subjectId), staleTime: Infinity }),
  });
}

/**
 * The firm-wide Ausschreibungen list (the sidebar screen): GET /placements with NO subject.
 * `enabled` false skips the fetch (the E5 idiom — a caller without `placement.read` must not
 * even request the data; the api guard stays the hard line).
 */
export function usePlacementsForFirm(enabled = true, filters: { view?: PlacementListView; q?: string } = {}) {
  const live = isLiveData();
  const view = filters.view ?? 'alle';
  const q = filters.q?.trim() ? filters.q.trim() : undefined;
  return useInfiniteQuery<PlacementFirmListResponse>({
    // view + q are part of the key (a new view/search resets the infinite query); the cursor is the
    // page param, NOT a key segment.
    queryKey: qk.placementsFirm({ view, q: q ?? null }, live ? 'live' : 'mock'),
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.page.next_cursor ?? undefined,
    queryFn: ({ pageParam }) =>
      live
        ? apiClient.placements.listAll({ view, q, cursor: pageParam as string | undefined })
        : Promise.resolve(mockFirmList()),
    ...(live
      ? {}
      : { initialData: { pages: [mockFirmList()], pageParams: [undefined as string | undefined] }, staleTime: Infinity }),
  });
}

/** One Placement's full detail (the AusschreibungScreen). */
export function usePlacement(id: string) {
  const live = isLiveData();
  return useQuery<PlacementDetail>({
    queryKey: qk.placement(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.get(id) : Promise.resolve(mockDetail(id))),
    ...(live ? {} : { initialData: () => mockDetail(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreatePlacement() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (body: PlacementCreateBody) =>
      live ? apiClient.placements.create(body) : Promise.resolve(mockDetail('mock-new', body)),
    onSuccess: (detail) => {
      // Prefix-invalidate the customer's list (the key carries filters + mode after these segments)
      // + the firm-wide list (the sidebar screen).
      void qc.invalidateQueries({ queryKey: ['placements', 'list', detail.subject_type, detail.subject_id] });
      void qc.invalidateQueries({ queryKey: ['placements', 'list', 'firm'] });
    },
  });
}

export function useAbbrechenPlacement(id: string) {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: (grund: string) =>
      live ? apiClient.placements.abbrechen(id, grund) : Promise.resolve(mockDetail(id)),
    onSuccess: (detail) => {
      void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['placements', 'list', detail.subject_type, detail.subject_id] });
      void qc.invalidateQueries({ queryKey: ['placements', 'list', 'firm'] });
    },
  });
}
